/**
 * RegistroGrupo Component
 * 
 * Componente para registrar calificaciones de estudiantes en un grupo.
 * Muestra tabla editable con estudiantes y sus calificaciones.
 * Integrado con APIs del backend.
 * 
 * Basado en Image 17 del diseño
 */

import React, { useState, useEffect, useMemo } from "react";
import styles from "./RegistroCalificaciones.module.css";
import { getEstudiantesGrupo, guardarCalificaciones, notifyCalificacionesUpdated } from "../../api/calificaciones";

function RegistroGrupo({ grupo, tipoEvaluacion, tiposEvaluacion = [], onCambioTipoEvaluacion, onRegresar, onAbrirReporte }) {
  const [estudiantes, setEstudiantes] = useState([]);
  const [calificaciones, setCalificaciones] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  // Metadata de bloqueo por fecha límite (devuelto por el backend en /calificaciones/estudiantes/<id>/)
  // Estructura esperada: { PARCIAL1: { bloqueado, fecha_limite, mensaje }, PARCIAL2: {...}, PARCIAL3: {...} }
  const [bloqueoParciales, setBloqueoParciales] = useState({});
  
  // Estado para ordenamiento - por defecto alfabético por nombre ascendente
  const [ordenamiento, setOrdenamiento] = useState({ campo: 'nombre', direccion: 'asc' });

  // Bloqueo de captura por fecha límite del parcial actual (solo aplica a PARCIAL1/2/3).
  // Se evalúa contra metadata enviada por el backend; Control Escolar usa otra pantalla.
  const tipoEvalNorm = (tipoEvaluacion || '').toUpperCase();
  const infoBloqueoActual = bloqueoParciales && bloqueoParciales[tipoEvalNorm] ? bloqueoParciales[tipoEvalNorm] : null;
  const parcialBloqueado = !!(infoBloqueoActual && infoBloqueoActual.bloqueado);
  const mensajeBloqueo = infoBloqueoActual && infoBloqueoActual.mensaje
    ? infoBloqueoActual.mensaje
    : 'La fecha límite de captura para este parcial ha finalizado.';

  // Estudiantes ordenados según el criterio seleccionado
  const estudiantesOrdenados = useMemo(() => {
    if (!estudiantes.length) return [];
    
    return [...estudiantes].sort((a, b) => {
      let valorA, valorB;
      
      if (ordenamiento.campo === 'nombre') {
        valorA = (a.nombre || '').toLowerCase();
        valorB = (b.nombre || '').toLowerCase();
      } else if (ordenamiento.campo === 'matricula') {
        valorA = a.matricula || '';
        valorB = b.matricula || '';
      }
      
      if (ordenamiento.direccion === 'asc') {
        return valorA < valorB ? -1 : valorA > valorB ? 1 : 0;
      } else {
        return valorA > valorB ? -1 : valorA < valorB ? 1 : 0;
      }
    });
  }, [estudiantes, ordenamiento]);

  // Función para cambiar ordenamiento
  const handleOrdenamiento = (campo) => {
    setOrdenamiento(prev => {
      if (prev.campo === campo) {
        // Si es el mismo campo, cambiar dirección
        return { campo, direccion: prev.direccion === 'asc' ? 'desc' : 'asc' };
      } else {
        // Si es diferente campo, empezar con ascendente
        return { campo, direccion: 'asc' };
      }
    });
  };

  // Obtener ícono de ordenamiento
  const getOrdenIcon = (campo) => {
    if (ordenamiento.campo !== campo) return '↕️';
    return ordenamiento.direccion === 'asc' ? '↑' : '↓';
  };

  // Cargar estudiantes desde la API
  useEffect(() => {
    const cargarEstudiantes = async () => {
      if (!grupo?.id_grupo) {
        console.log('[RegistroGrupo] No hay id_grupo en el objeto grupo:', grupo);
        return;
      }
      
      console.log('[RegistroGrupo] Cargando estudiantes para grupo_id:', grupo.id_grupo, 'Grupo completo:', grupo);
      
      try {
        setLoading(true);
        const data = await getEstudiantesGrupo(grupo.id_grupo, tipoEvaluacion);
        console.log('[RegistroGrupo] Estudiantes cargados:', data);
        
        setEstudiantes(data.estudiantes || []);
        setBloqueoParciales(data.bloqueo_parciales || {});
        
        // Inicializar calificaciones con datos existentes
        const calificacionesIniciales = {};
        (data.estudiantes || []).forEach(est => {
          calificacionesIniciales[est.id_estudiante_grupo] = {
            calificacion: est.calificacion?.calificacion || est.calificacion?.promedio || 0,
            observaciones: est.calificacion?.observaciones || ''
          };
        });
        setCalificaciones(calificacionesIniciales);
      } catch (error) {
        console.error('[RegistroGrupo] Error al cargar estudiantes:', error);
        setMensaje({ tipo: 'error', texto: 'Error al cargar estudiantes del grupo' });
      } finally {
        setLoading(false);
      }
    };
    
    cargarEstudiantes();
  }, [grupo, tipoEvaluacion]);

  const handleCambioCalificacion = (estudianteGrupoId, campo, valor) => {
    setCalificaciones(prev => {
      const nuevas = { ...prev };
      
      // Validar calificación: solo enteros del 1 al 10
      if (campo === 'calificacion') {
        // Solo permitir valores numéricos enteros
        const valorNumerico = parseInt(valor, 10);
        if (valor === '' || valor === null) {
          nuevas[estudianteGrupoId] = {
            ...nuevas[estudianteGrupoId],
            calificacion: ''
          };
        } else if (!isNaN(valorNumerico) && valorNumerico >= 0 && valorNumerico <= 10) {
          nuevas[estudianteGrupoId] = {
            ...nuevas[estudianteGrupoId],
            calificacion: valorNumerico
          };
        }
        // Si el valor no es válido, no se actualiza
      } else {
        nuevas[estudianteGrupoId] = {
          ...nuevas[estudianteGrupoId],
          [campo]: valor
        };
      }
      
      return nuevas;
    });
  };

  // Handler para pegar calificaciones (soporta pegar múltiples valores desde Excel)
  const handlePaste = (e, estudianteIndex) => {
    const pastedText = e.clipboardData.getData('text');
    // Separar por saltos de línea o tabulaciones (cuando se pega desde Excel)
    const valores = pastedText.split(/[\n\r\t]+/).map(v => v.trim()).filter(v => v !== '');
    
    if (valores.length > 1) {
      // Pegar múltiples valores llenando hacia abajo
      e.preventDefault();
      setCalificaciones(prev => {
        const nuevas = { ...prev };
        valores.forEach((val, i) => {
          const idx = estudianteIndex + i;
          if (idx < estudiantesOrdenados.length) {
            const est = estudiantesOrdenados[idx];
            const num = parseInt(val, 10);
            if (!isNaN(num) && num >= 0 && num <= 10) {
              nuevas[est.id_estudiante_grupo] = {
                ...nuevas[est.id_estudiante_grupo],
                calificacion: num
              };
            }
          }
        });
        return nuevas;
      });
    } else if (valores.length === 1) {
      // Pegar un solo valor — dejar que el onChange lo procese
      e.preventDefault();
      const num = parseInt(valores[0], 10);
      const est = estudiantesOrdenados[estudianteIndex];
      if (!isNaN(num) && num >= 0 && num <= 10) {
        handleCambioCalificacion(est.id_estudiante_grupo, 'calificacion', String(num));
      }
    }
  };

  const handleGuardar = async () => {
    // Defensa frontend: no permitir guardado si el parcial está bloqueado por fecha límite.
    if (parcialBloqueado) {
      setMensaje({ tipo: 'error', texto: mensajeBloqueo });
      return;
    }
    setGuardando(true);
    setMensaje({ tipo: '', texto: '' });
    
    try {
      // Preparar datos para enviar - validar que sean enteros del 1 al 10
      const calificacionesArray = estudiantes.map(est => {
        let calif = calificaciones[est.id_estudiante_grupo]?.calificacion;
        // Convertir a entero y validar rango 1-10
        calif = parseInt(calif, 10);
        if (isNaN(calif) || calif < 0) calif = 0;
        if (calif > 10) calif = 10;
        
        return {
          id_estudiante_grupo: est.id_estudiante_grupo,
          calificacion: calif,
          observaciones: calificaciones[est.id_estudiante_grupo]?.observaciones || ''
        };
      });
      
      const resultado = await guardarCalificaciones({
        id_grupo: grupo.id_grupo,
        tipo_evaluacion: tipoEvaluacion,
        calificaciones: calificacionesArray
      });
      
      console.log('[RegistroGrupo] Resultado guardado:', resultado);
      notifyCalificacionesUpdated(estudiantes.map(est => est.id_estudiante));
      setMensaje({ tipo: 'success', texto: resultado.message || 'Calificaciones guardadas exitosamente' });
    } catch (error) {
      console.error('[RegistroGrupo] Error al guardar:', error);
      setMensaje({ tipo: 'error', texto: error.message || 'Error al guardar calificaciones' });
    } finally {
      setGuardando(false);
    }
  };

  const getTipoEvaluacionLabel = () => {
    const labels = {
      'PARCIAL1': 'Parcial 1',
      'PARCIAL2': 'Parcial 2',
      'PARCIAL3': 'Parcial 3',
      'FINAL': 'Final',
      'EXTRAORDINARIO': 'Extraordinario'
    };
    return labels[tipoEvaluacion?.toUpperCase()] || tipoEvaluacion;
  };


  // Handler para abrir el reporte: pide todas las calificaciones de todos los parciales
  const handleAbrirReporteLocal = async () => {
    try {
      setLoading(true);
      // Pedir todas las calificaciones de todos los parciales
      const data = await getEstudiantesGrupo(grupo.id_grupo, 'ALL');
      // Log para depuración: ver estructura de estudiantes y sus calificaciones
      console.log('[Reporte] data.estudiantes:', data.estudiantes);
      // Si existe calificaciones por parcial, úsalo; si no, usa calificacion directo (PARCIAL 1)
      const estudiantesParaReporte = (data.estudiantes || []).map(est => {
        // Si existe calificaciones por parcial (estructura avanzada)
        if (est.calificaciones) {
          const cal = est.calificaciones;
          return {
            id: est.id_estudiante_grupo,
            nombre: est.nombre,
            matricula: est.matricula,
            a1: cal['PARCIAL 1']?.actividad1 ?? cal['PARCIAL 1']?.calificacion_actividades ?? '',
            ex1: cal['PARCIAL 1']?.examen ?? cal['PARCIAL 1']?.calificacion_examen ?? '',
            parcial1: cal['PARCIAL 1']?.calificacion ?? cal['PARCIAL 1']?.promedio ?? cal['PARCIAL 1']?.calificacion_final ?? '',
            a2: cal['PARCIAL 2']?.actividad1 ?? cal['PARCIAL 2']?.calificacion_actividades ?? '',
            ex2: cal['PARCIAL 2']?.examen ?? cal['PARCIAL 2']?.calificacion_examen ?? '',
            parcial2: cal['PARCIAL 2']?.calificacion ?? cal['PARCIAL 2']?.promedio ?? cal['PARCIAL 2']?.calificacion_final ?? '',
            a3: cal['PARCIAL 3']?.actividad1 ?? cal['PARCIAL 3']?.calificacion_actividades ?? '',
            ex3: cal['PARCIAL 3']?.examen ?? cal['PARCIAL 3']?.calificacion_examen ?? '',
            parcial3: cal['PARCIAL 3']?.calificacion ?? cal['PARCIAL 3']?.promedio ?? cal['PARCIAL 3']?.calificacion_final ?? '',
            promedio: '',
          };
        } else {
          // Si solo existe calificacion (estructura simple, solo PARCIAL 1)
          return {
            id: est.id_estudiante_grupo,
            nombre: est.nombre,
            matricula: est.matricula,
            a1: est.calificacion?.actividad1 ?? est.calificacion?.calificacion_actividades ?? '',
            ex1: est.calificacion?.examen ?? est.calificacion?.calificacion_examen ?? '',
            parcial1: est.calificacion?.promedio ?? est.calificacion?.calificacion ?? est.calificacion?.calificacion_final ?? '',
            a2: '',
            ex2: '',
            parcial2: '',
            a3: '',
            ex3: '',
            parcial3: '',
            promedio: '',
          };
        }
      });
      console.log('[Reporte] estudiantesParaReporte:', estudiantesParaReporte);
      onAbrirReporte(estudiantesParaReporte);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al cargar calificaciones para el reporte' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.registroContainer}>
      {/* Card de Búsqueda (minimizado) */}
      <div className={styles.busquedaCard}>
        <div className={styles.busquedaHeader}>
          <div className={styles.busquedaIcono}>
            <span>🔍</span>
          </div>
          <h2 className={styles.busquedaTitulo}>Búsqueda de Grupo</h2>
        </div>

        {/* Filtros - Tipo de Evaluación editable */}
        <div className={styles.filtrosContainer}>
          <div className={styles.filtroGroup}>
            <label className={styles.filtroLabel}>Campus</label>
            <select className={styles.filtroSelect} disabled>
              <option>{grupo?.campus || 'Sin campus'}</option>
            </select>
          </div>

          <div className={styles.filtroGroup}>
            <label className={styles.filtroLabel}>Tipo de Evaluación</label>
            <select 
              className={styles.filtroSelect}
              value={tipoEvaluacion || ''}
              onChange={(e) => onCambioTipoEvaluacion && onCambioTipoEvaluacion(e.target.value)}
            >
              {tiposEvaluacion.length > 0 ? (
                tiposEvaluacion.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))
              ) : (
                <option value={tipoEvaluacion}>{getTipoEvaluacionLabel()}</option>
              )}
            </select>
          </div>
        </div>

        {/* Botones de acción */}
        <div className={styles.botonesAccion}>
          <button 
            className={styles.buscarButton}
            onClick={onRegresar}
          >
            BUSCAR
          </button>
          <button 
            className={styles.regresarButton}
            onClick={onRegresar}
          >
            REGRESAR
          </button>
        </div>
      </div>

      {/* Card de Grupo */}
      <div className={styles.grupoCard}>
        <div className={styles.grupoHeader}>
          <div className={styles.grupoIcono}>
            <span>👥</span>
          </div>
          <div className={styles.grupoInfo}>
            <h3 className={styles.grupoTitulo}>
              Grupo: {grupo?.codigo} - {grupo?.nombre}
            </h3>
            <p className={styles.grupoHorario}>
              Horario: {grupo?.horario || 'Sin horario asignado'}
            </p>
          </div>
        </div>

        {/* Mensaje de estado */}
        {mensaje.texto && (
          <div className={mensaje.tipo === 'error' ? styles.errorMessage : styles.successMessage}>
            {mensaje.texto}
          </div>
        )}

        {/* Banner de bloqueo por fecha límite del parcial */}
        {parcialBloqueado && (
          <div
            className={styles.errorMessage}
            style={{
              background: '#fff4e5',
              border: '1px solid #ffb74d',
              color: '#8a4b00',
              padding: '10px 14px',
              borderRadius: '6px',
              fontWeight: 600,
              margin: '8px 0'
            }}
          >
            🔒 {mensajeBloqueo} La captura para este parcial está deshabilitada.
          </div>
        )}

        {/* Tabla de Calificaciones */}
        <div className={styles.tablaCalificacionesContainer}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <p>Cargando estudiantes...</p>
            </div>
          ) : estudiantes.length === 0 ? (
            <div className={styles.emptyContainer}>
              <p>No hay estudiantes inscritos en este grupo.</p>
            </div>
          ) : (
            <table className={styles.tablaCalificaciones}>
              <thead>
                <tr className={styles.tablaCalificacionesHeader}>
                  <th 
                    onClick={() => handleOrdenamiento('nombre')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Ordenar por nombre"
                  >
                    Estudiante {getOrdenIcon('nombre')}
                  </th>
                  <th 
                    onClick={() => handleOrdenamiento('matricula')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Ordenar por matrícula"
                  >
                    Matrícula {getOrdenIcon('matricula')}
                  </th>
                  <th>Calificación</th>
                  <th>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {estudiantesOrdenados.map((estudiante, index) => (
                  <tr key={estudiante.id_estudiante_grupo} className={styles.tablaCalificacionesFila}>
                    <td className={styles.estudianteNombre}>
                      {estudiante.nombre}
                    </td>
                    <td>{estudiante.matricula}</td>
                    <td>
                      <input
                        type="text"
                        inputMode="decimal"
                        className={styles.inputCalificacion}
                        value={calificaciones[estudiante.id_estudiante_grupo]?.calificacion ?? ''}
                        onChange={(e) => handleCambioCalificacion(estudiante.id_estudiante_grupo, 'calificacion', e.target.value)}
                        placeholder="0-10"
                        disabled={parcialBloqueado}
                        title={parcialBloqueado ? mensajeBloqueo : undefined}
                        onPaste={(e) => handlePaste(e, index)}
                        onKeyDown={(e) => {
                          // Permitir: Ctrl/Cmd shortcuts (copiar, pegar, cortar, seleccionar todo)
                          if (e.ctrlKey || e.metaKey) return;
                          // Permitir: teclas de navegación, borrar, tab, punto y coma decimal
                          if (["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End", ".", ","].includes(e.key)) return;
                          // Permitir solo dígitos y un punto/decimal
                          if (!/^\d$/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className={styles.inputObservaciones}
                        value={calificaciones[estudiante.id_estudiante_grupo]?.observaciones || ''}
                        onChange={(e) => handleCambioCalificacion(estudiante.id_estudiante_grupo, 'observaciones', e.target.value)}
                        placeholder=""
                        disabled={parcialBloqueado}
                        title={parcialBloqueado ? mensajeBloqueo : undefined}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Botones de acción inferior */}
        <div className={styles.botonesInferiores}>
          <button 
            className={styles.guardarButton}
            onClick={handleGuardar}
            disabled={guardando || loading || estudiantes.length === 0 || parcialBloqueado}
            title={parcialBloqueado ? mensajeBloqueo : undefined}
          >
            {guardando ? 'GUARDANDO...' : 'GUARDAR CALIFICACIONES'}
          </button>
          <button 
            className={styles.reporteButton}
            onClick={handleAbrirReporteLocal}
          >
            REPORTE DE CALIFICACIONES
          </button>
        </div>
      </div>
    </div>
  );
}

export default RegistroGrupo;
